import {
  AdditionalPermissionNames,
  BaseRoleNames,
  type BaseRoleNameValues,
  ResourceSet,
  SitePermissions,
  type RequestPermissions,
  UxPermission,
  UxPermissionGroupNames,
  UxPermissionSubGroupNames,
  type AdditionalPermission,
  type PermissionData,
  type FeatureFlagsMap,
  type PermissionParameters,
  type PermissionParameterList,
  CommenterSitePermissions,
  DesignerSitePermissions,
  EditorSitePermissions,
  MarketerSitePermissions,
  TIER_CUSTOM_BASE_ROLES,
  PERMISSION_DESCRIPTIONS,
} from '@packages/domains/authorization/permissions/catalog';
import {type WorkspaceSeatType} from '@packages/domains/subscription-and-payments/workspace-seats/data-types';
import {FEATURE_FLAGS} from '@packages/systems/feature-config/shared';
import {isValidCustomBaseRoleName} from './base-roles';

// MAKE_CHANGES minus its CMS rules.
const PAGE_EDITS_RULES: Array<PermissionData> = [
  {resource: 'site', action: 'design'},
  {resource: 'siteBranch', action: 'merge'},
  {
    resource: 'siteDesignerModes',
    action: 'view',
    isCheckedRule: (checked: boolean) => !checked,
  },
  {resource: 'sitePage', action: 'editContent'},
];

// siteCollection.manage is left out: Content Editors don't hold it, so they
// must not gain it from the CMS checkbox. Designers and Marketers get it
// alongside these, as "Make changes without approval" granted it to both.
const CMS_ITEM_AND_COMMERCE_RULES: Array<PermissionData> = [
  {resource: 'siteCollectionItem', action: 'manage'},
  {resource: 'siteCommerce', action: 'manage'},
  {resource: 'siteCommerceItem', action: 'manage'},
];

export const ADDITIONAL_PERMISSIONS_LIST: Array<UxPermission> = [
  [
    AdditionalPermissionNames.PUBLISH_TO_PRODUCTION,
    {
      uxBaseRoleFilter: (role: string) =>
        role === BaseRoleNames.DESIGN ||
        role === BaseRoleNames.BUILD_PAGES ||
        role === BaseRoleNames.EDIT_CONTENT,
      resource: 'site',
      action: 'publish',
      group: UxPermissionGroupNames.PUBLISHING,
      subgroup: UxPermissionSubGroupNames.FULL_SITE,
      uxDependentsToSelect: [
        {
          dependentPermissionName: AdditionalPermissionNames.PUBLISH_TO_STAGING,
        },
        {
          dependentPermissionName:
            AdditionalPermissionNames.PUBLISH_CMS_TO_PRODUCTION,
        },
        {
          dependentPermissionName:
            AdditionalPermissionNames.QUEUE_CMS_FOR_PUBLISH,
        },
        {
          dependentPermissionName:
            AdditionalPermissionNames.SPP_PUBLISH_TO_PRODUCTION,
        },
        {
          dependentPermissionName:
            AdditionalPermissionNames.SPP_PUBLISH_TO_STAGING,
        },
      ],
    },
  ],
  [
    AdditionalPermissionNames.PUBLISH_TO_STAGING,
    {
      uxBaseRoleFilter: (role: string) =>
        role === BaseRoleNames.DESIGN ||
        role === BaseRoleNames.BUILD_PAGES ||
        role === BaseRoleNames.EDIT_CONTENT,
      resource: 'site',
      action: 'subPublish',
      group: UxPermissionGroupNames.PUBLISHING,
      subgroup: UxPermissionSubGroupNames.FULL_SITE,
      uxDependentsToSelect: [
        {
          dependentPermissionName:
            AdditionalPermissionNames.QUEUE_CMS_FOR_PUBLISH,
        },
        {
          dependentPermissionName:
            AdditionalPermissionNames.SPP_PUBLISH_TO_STAGING,
        },
      ],
    },
  ],
  [
    AdditionalPermissionNames.PUBLISH_CMS_TO_PRODUCTION,
    {
      uxBaseRoleFilter: (role: string) =>
        role === BaseRoleNames.DESIGN ||
        role === BaseRoleNames.BUILD_PAGES ||
        role === BaseRoleNames.EDIT_CONTENT,
      ruleSet: [
        {resource: 'siteCollectionItem', action: 'publish'},
        {resource: 'siteCommerceItem', action: 'publish'},
      ],
      group: UxPermissionGroupNames.PUBLISHING,
      subgroup: UxPermissionSubGroupNames.FULL_SITE,
      uxDependentsToSelect: [
        {
          dependentPermissionName:
            AdditionalPermissionNames.QUEUE_CMS_FOR_PUBLISH,
        },
      ],
    },
  ],
  [
    AdditionalPermissionNames.QUEUE_CMS_FOR_PUBLISH,
    {
      uxBaseRoleFilter: (role: string) =>
        role === BaseRoleNames.DESIGN ||
        role === BaseRoleNames.BUILD_PAGES ||
        role === BaseRoleNames.EDIT_CONTENT,
      ruleSet: [
        {resource: 'siteCollectionItem', action: 'queue'},
        {resource: 'siteCommerceItem', action: 'queue'},
      ],
      group: UxPermissionGroupNames.PUBLISHING,
      subgroup: UxPermissionSubGroupNames.FULL_SITE,
    },
  ],
  [
    AdditionalPermissionNames.SPP_PUBLISH_TO_PRODUCTION,
    {
      uxBaseRoleFilter: (role: string) =>
        role === BaseRoleNames.DESIGN ||
        role === BaseRoleNames.BUILD_PAGES ||
        role === BaseRoleNames.EDIT_CONTENT,
      resource: 'sitePage',
      action: 'sppPublishToProduction',
      group: UxPermissionGroupNames.PUBLISHING,
      subgroup: UxPermissionSubGroupNames.INDIVIDUAL_PAGES,
      uxDependentsToSelect: [
        {
          dependentPermissionName:
            AdditionalPermissionNames.SPP_PUBLISH_TO_STAGING,
        },
      ],
    },
  ],
  [
    AdditionalPermissionNames.SPP_PUBLISH_TO_STAGING,
    {
      uxBaseRoleFilter: (role: string) =>
        role === BaseRoleNames.DESIGN ||
        role === BaseRoleNames.BUILD_PAGES ||
        role === BaseRoleNames.EDIT_CONTENT,
      resource: 'sitePage',
      action: 'sppPublishToStaging',
      group: UxPermissionGroupNames.PUBLISHING,
      subgroup: UxPermissionSubGroupNames.INDIVIDUAL_PAGES,
    },
  ],
  [
    AdditionalPermissionNames.APPROVE_CHANGES,
    {
      resource: 'site',
      action: 'approve',
      group: UxPermissionGroupNames.BUILD_AND_REVIEW,
    },
  ],
  [
    AdditionalPermissionNames.EDIT_COMPONENT_CONTENT,
    {
      uxBaseRoleFilter: (role: string) =>
        role === BaseRoleNames.BUILD_PAGES ||
        role === BaseRoleNames.EDIT_CONTENT,
      group: UxPermissionGroupNames.BUILD_AND_REVIEW,
      ruleSet: [{resource: 'siteSymbol', action: 'modify'}],
    },
  ],
  [
    AdditionalPermissionNames.CREATE_PAGES_WITHOUT_TEMPLATE,
    {
      group: UxPermissionGroupNames.BUILD_AND_REVIEW,
      uxBaseRoleFilter: (role: string) => role === BaseRoleNames.BUILD_PAGES,
      ruleSet: [
        {resource: 'sitePage', action: 'create'},
        {
          resource: 'siteBranch',
          action: 'createStandalone',
        },
      ],
    },
  ],
  [
    AdditionalPermissionNames.MAKE_CHANGES,
    {
      uxBaseRoleFilter: (role: string) =>
        role === BaseRoleNames.DESIGN || role === BaseRoleNames.BUILD_PAGES,
      group: UxPermissionGroupNames.BUILD_AND_REVIEW,
      hiddenByFeatureFlags: [FEATURE_FLAGS.SPLIT_APPROVAL_CHECKBOXES],
      ruleSet: [
        {resource: 'site', action: 'design'},
        {resource: 'siteBranch', action: 'merge'},
        {
          resource: 'siteDesignerModes',
          action: 'view',
          isCheckedRule: (checked: boolean) => !checked,
        },
        {resource: 'siteCollection', action: 'manage'},
        {resource: 'siteCollectionItem', action: 'manage'},
        {resource: 'siteCommerce', action: 'manage'},
        {resource: 'siteCommerceItem', action: 'manage'},
        {resource: 'sitePage', action: 'editContent'},
      ],
      uxDependentsToSelect: [
        {
          dependentPermissionName: AdditionalPermissionNames.MERGE_APPROVED,
        },
      ],
      uxDependentsToDisable: [
        {
          dependentPermissionName:
            AdditionalPermissionNames.QUEUE_CMS_FOR_PUBLISH,
          reason: `Required when "${PERMISSION_DESCRIPTIONS[AdditionalPermissionNames.MAKE_CHANGES].label}" is selected.`,
        },
      ],
    },
  ],
  [
    AdditionalPermissionNames.MERGE_APPROVED,
    {
      uxBaseRoleFilter: (role: string) =>
        role === BaseRoleNames.DESIGN ||
        role === BaseRoleNames.BUILD_PAGES ||
        role === BaseRoleNames.EDIT_CONTENT,
      resource: 'siteBranch',
      action: 'mergeApproved',
      group: UxPermissionGroupNames.BUILD_AND_REVIEW,
      requiredFeatureFlags: [FEATURE_FLAGS.MERGE_APPROVED_BRANCH],
    },
  ],
  [
    AdditionalPermissionNames.EDIT_MAIN_SITE_WITHOUT_APPROVAL,
    {
      uxBaseRoleFilter: (role: string) =>
        role === BaseRoleNames.DESIGN ||
        role === BaseRoleNames.BUILD_PAGES ||
        role === BaseRoleNames.EDIT_CONTENT,
      group: UxPermissionGroupNames.BUILD_AND_REVIEW,
      requiredFeatureFlags: [FEATURE_FLAGS.SPLIT_APPROVAL_CHECKBOXES],
      ruleSetByBaseRole: {
        [BaseRoleNames.DESIGN]: PAGE_EDITS_RULES,
        [BaseRoleNames.BUILD_PAGES]: PAGE_EDITS_RULES,
        // Content Editors already edit pages on main, so checked has to match
        // their base role: merging a branch is never granted here, in either
        // state, and siteBranch.mergeApproved stays with MERGE_APPROVED.
        [BaseRoleNames.EDIT_CONTENT]: [
          {resource: 'site', action: 'design'},
          {
            resource: 'siteDesignerModes',
            action: 'view',
            isCheckedRule: (checked: boolean) => !checked,
          },
          {resource: 'sitePage', action: 'editContent'},
          {
            resource: 'siteBranch',
            action: 'merge',
            isCheckedRule: () => false,
          },
        ],
      },
      uxDependentsToSelectByBaseRole: {
        [BaseRoleNames.DESIGN]: [
          {dependentPermissionName: AdditionalPermissionNames.MERGE_APPROVED},
        ],
        [BaseRoleNames.BUILD_PAGES]: [
          {dependentPermissionName: AdditionalPermissionNames.MERGE_APPROVED},
        ],
        [BaseRoleNames.EDIT_CONTENT]: [],
      },
    },
  ],
  [
    AdditionalPermissionNames.EDIT_CMS_ITEMS,
    {
      uxBaseRoleFilter: (role: string) =>
        role === BaseRoleNames.DESIGN ||
        role === BaseRoleNames.BUILD_PAGES ||
        role === BaseRoleNames.EDIT_CONTENT,
      group: UxPermissionGroupNames.CMS,
      requiredFeatureFlags: [FEATURE_FLAGS.SPLIT_APPROVAL_CHECKBOXES],
      // Capped by the base role: Designers and Marketers hold
      // siteCollection.manage, as they did under MAKE_CHANGES, so re-saving an
      // existing role keeps it. Content Editors never held it.
      ruleSetByBaseRole: {
        [BaseRoleNames.DESIGN]: [
          {resource: 'siteCollection', action: 'manage'},
          ...CMS_ITEM_AND_COMMERCE_RULES,
        ],
        [BaseRoleNames.BUILD_PAGES]: [
          {resource: 'siteCollection', action: 'manage'},
          ...CMS_ITEM_AND_COMMERCE_RULES,
        ],
        [BaseRoleNames.EDIT_CONTENT]: CMS_ITEM_AND_COMMERCE_RULES,
      },
    },
  ],
  [
    AdditionalPermissionNames.MANAGE_VARIABLES,
    {
      action: 'manage',
      group: UxPermissionGroupNames.BUILD_AND_REVIEW,
      resource: 'siteVariable',
      uxBaseRoleFilter: (role: string) => role === BaseRoleNames.DESIGN,
    },
  ],
  [
    AdditionalPermissionNames.MANAGE_CLASSES,
    {
      action: 'modify',
      group: UxPermissionGroupNames.BUILD_AND_REVIEW,
      resource: 'siteStyleBlock',
      uxBaseRoleFilter: (role: string) => role === BaseRoleNames.DESIGN,
    },
  ],
  [
    AdditionalPermissionNames.MANAGE_COMPONENTS,
    {
      group: UxPermissionGroupNames.BUILD_AND_REVIEW,
      uxBaseRoleFilter: (role: string) => role === BaseRoleNames.DESIGN,
      ruleSet: [
        {resource: 'siteSymbol', action: 'modify'},
        {resource: 'siteSymbol', action: 'create'},
        {resource: 'siteSymbol', action: 'manage'},
      ],
    },
  ],
  [
    AdditionalPermissionNames.USE_AI_FEATURES,
    {
      uxBaseRoleFilter: (role: string) =>
        role === BaseRoleNames.DESIGN ||
        role === BaseRoleNames.BUILD_PAGES ||
        role === BaseRoleNames.EDIT_CONTENT,
      resource: 'siteAI',
      action: 'use',
      group: UxPermissionGroupNames.AI_FEATURES,
    },
  ],
  [
    AdditionalPermissionNames.MANAGE_SITE_SETTINGS,
    {
      uxBaseRoleFilter: (role: string) =>
        role === BaseRoleNames.DESIGN ||
        role === BaseRoleNames.BUILD_PAGES ||
        role === BaseRoleNames.EDIT_CONTENT,
      ruleSet: [
        {resource: 'site', action: 'update'},
        {resource: 'siteCustomDomain', action: 'manage'},
        {resource: 'siteRedirects', action: 'read'},
        {resource: 'siteBilling', action: 'read'},
        {resource: 'siteLibrary', action: 'manage'},
        {resource: 'siteSnapshot', action: 'manage'},
        {resource: 'siteCustomCode', action: 'manage'},
        {resource: 'siteLocale', action: 'manage'},
      ],
      group: UxPermissionGroupNames.SITE_MANAGEMENT,
    },
  ],
];

// swaps in the base role's own ruleSet and dependents where the permission
// defines them, so callers can keep reading ruleSet and uxDependentsToSelect.
// An empty ruleSet override falls back to the default: the editor reads a
// permission with no rules back as checked. Dependents can be overridden with
// an empty list. A role rule set replaces the default resource/action pair as
// well as the default ruleSet, so the resolved entry never carries both.
export function resolvePermissionForBaseRole(
  [permissionName, permissionData]: UxPermission,
  baseRole: string
): UxPermission {
  const {ruleSetByBaseRole, uxDependentsToSelectByBaseRole, ...rest} =
    permissionData;
  // Own keys only, so a role string like "constructor" can't pick up an
  // inherited Object.prototype member.
  const roleRuleSet =
    ruleSetByBaseRole && Object.hasOwn(ruleSetByBaseRole, baseRole)
      ? ruleSetByBaseRole[baseRole as BaseRoleNameValues]
      : undefined;
  const roleDependents =
    uxDependentsToSelectByBaseRole &&
    Object.hasOwn(uxDependentsToSelectByBaseRole, baseRole)
      ? uxDependentsToSelectByBaseRole[baseRole as BaseRoleNameValues]
      : undefined;

  const {resource, action, ...restWithoutPair} = rest;

  return [
    permissionName,
    {
      ...(roleRuleSet?.length ? restWithoutPair : rest),
      ...(roleRuleSet?.length ? {ruleSet: roleRuleSet} : {}),
      ...(roleDependents && {uxDependentsToSelect: roleDependents}),
    },
  ];
}

type LayoutItem = {
  name: AdditionalPermission;
  group: UxPermissionGroupNames;
  subgroup?: UxPermissionSubGroupNames;
};

// Order and placement for the Build & review and CMS sections while the split
// flag is on: branch permissions first in workflow order, then design
// capabilities, then the CMS section.
const SPLIT_APPROVAL_LAYOUT: Array<LayoutItem> = [
  {
    name: AdditionalPermissionNames.EDIT_MAIN_SITE_WITHOUT_APPROVAL,
    group: UxPermissionGroupNames.BUILD_AND_REVIEW,
    subgroup: UxPermissionSubGroupNames.BRANCHES,
  },
  {
    name: AdditionalPermissionNames.APPROVE_CHANGES,
    group: UxPermissionGroupNames.BUILD_AND_REVIEW,
    subgroup: UxPermissionSubGroupNames.BRANCHES,
  },
  {
    name: AdditionalPermissionNames.MERGE_APPROVED,
    group: UxPermissionGroupNames.BUILD_AND_REVIEW,
    subgroup: UxPermissionSubGroupNames.BRANCHES,
  },
  {
    name: AdditionalPermissionNames.EDIT_COMPONENT_CONTENT,
    group: UxPermissionGroupNames.BUILD_AND_REVIEW,
    subgroup: UxPermissionSubGroupNames.DESIGN,
  },
  {
    name: AdditionalPermissionNames.CREATE_PAGES_WITHOUT_TEMPLATE,
    group: UxPermissionGroupNames.BUILD_AND_REVIEW,
    subgroup: UxPermissionSubGroupNames.DESIGN,
  },
  {
    name: AdditionalPermissionNames.MANAGE_VARIABLES,
    group: UxPermissionGroupNames.BUILD_AND_REVIEW,
    subgroup: UxPermissionSubGroupNames.DESIGN,
  },
  {
    name: AdditionalPermissionNames.MANAGE_CLASSES,
    group: UxPermissionGroupNames.BUILD_AND_REVIEW,
    subgroup: UxPermissionSubGroupNames.DESIGN,
  },
  {
    name: AdditionalPermissionNames.MANAGE_COMPONENTS,
    group: UxPermissionGroupNames.BUILD_AND_REVIEW,
    subgroup: UxPermissionSubGroupNames.DESIGN,
  },
  {
    name: AdditionalPermissionNames.EDIT_CMS_ITEMS,
    group: UxPermissionGroupNames.CMS,
  },
  {
    name: AdditionalPermissionNames.PUBLISH_CMS_TO_PRODUCTION,
    group: UxPermissionGroupNames.CMS,
  },
  {
    name: AdditionalPermissionNames.QUEUE_CMS_FOR_PUBLISH,
    group: UxPermissionGroupNames.CMS,
  },
];

// Placement for the Admin section while the group-admin flag is on: site
// settings first, then AI.
const ADMIN_LAYOUT: Array<LayoutItem> = [
  {
    name: AdditionalPermissionNames.MANAGE_SITE_SETTINGS,
    group: UxPermissionGroupNames.ADMIN,
  },
  {
    name: AdditionalPermissionNames.USE_AI_FEATURES,
    group: UxPermissionGroupNames.ADMIN,
  },
];

// These two sit among the Publishing items in the list, so they are lifted out
// of it without moving where the layout block lands.
const RELOCATED_FROM_PUBLISHING = new Set<AdditionalPermission>([
  AdditionalPermissionNames.QUEUE_CMS_FOR_PUBLISH,
  AdditionalPermissionNames.PUBLISH_CMS_TO_PRODUCTION,
]);

// The layout items sit in one block of the list, so everything else keeps its
// place on either side of it.
function applyLayout(
  permissions: Array<UxPermission>,
  layout: Array<LayoutItem>
): Array<UxPermission> {
  const layoutByName = new Map(layout.map((item) => [item.name, item]));
  const firstLayoutIndex = permissions.findIndex(
    ([name]) => layoutByName.has(name) && !RELOCATED_FROM_PUBLISHING.has(name)
  );
  if (firstLayoutIndex === -1) {
    return permissions;
  }

  const layoutItems = layout.flatMap(
    ({name, group, subgroup}): Array<UxPermission> => {
      const permission = permissions.find(
        ([permissionName]) => permissionName === name
      );
      return permission ? [[name, {...permission[1], group, subgroup}]] : [];
    }
  );
  const isOutsideLayout = ([name]: UxPermission) => !layoutByName.has(name);

  return [
    ...permissions.slice(0, firstLayoutIndex).filter(isOutsideLayout),
    ...layoutItems,
    ...permissions.slice(firstLayoutIndex).filter(isOutsideLayout),
  ];
}

export function filterPermissionsByRole(
  selectedRole: string,
  featureFlags?: FeatureFlagsMap
) {
  const permissions = ADDITIONAL_PERMISSIONS_LIST.filter(
    (permission: UxPermission) =>
      applyPermissionFilters(selectedRole, permission, featureFlags)
  ).map((permission: UxPermission) =>
    resolvePermissionForBaseRole(permission, selectedRole)
  );

  const splitLayout = featureFlags?.[FEATURE_FLAGS.SPLIT_APPROVAL_CHECKBOXES]
    ? applyLayout(permissions, SPLIT_APPROVAL_LAYOUT)
    : permissions;

  return featureFlags?.[FEATURE_FLAGS.GROUP_ADMIN_CHECKBOXES]
    ? applyLayout(splitLayout, ADMIN_LAYOUT)
    : splitLayout;
}

// turns the uxName:checked pairs into the corresponding permissions
function getCheckedProperties(
  requestPermissions: RequestPermissions,
  baseRole: BaseRoleNameValues
) {
  const bigList: PermissionParameterList = [];
  const checkedWithSubset: Array<Array<PermissionData>> = [];
  const uncheckedWithSubset: Array<Array<PermissionData>> = [];

  const uxToDataMap = new Map(
    ADDITIONAL_PERMISSIONS_LIST.map((permission) =>
      resolvePermissionForBaseRole(permission, baseRole)
    )
  );
  Object.entries(requestPermissions).forEach(([uxName, isChecked]) => {
    const dataLocation = uxToDataMap.get(uxName as AdditionalPermission);
    if (!dataLocation) {
      throw new Error('not found');
    }

    // logic for subset
    if (dataLocation.ruleSet) {
      const subsetList = isChecked ? checkedWithSubset : uncheckedWithSubset;
      subsetList.push(dataLocation.ruleSet);
    } else if (dataLocation.action && dataLocation.resource) {
      // logic for no subset
      const updatedData = Object.assign({}, dataLocation, {checked: isChecked});
      // @ts-expect-error this is qualified in the if statement
      bigList.push(updatedData);
    }
  });

  let checkedSubsets: PermissionParameterList = [];
  if (checkedWithSubset.length) {
    checkedSubsets = handleSubset(checkedWithSubset, true);
  }

  let uncheckedSubsets: PermissionParameterList = [];
  if (uncheckedWithSubset.length) {
    uncheckedSubsets = handleSubset(uncheckedWithSubset, false);
  }

  return bigList.concat(checkedSubsets, uncheckedSubsets);
}

// flatten the array of subset set arrays and return a one dimensional array
function handleSubset(
  subsetList: Array<Array<PermissionData>>,
  isChecked: boolean
): PermissionParameterList {
  const flattened: Array<PermissionData> = subsetList.flat();
  return flattened.map((subset) => {
    const checked = subset?.isCheckedRule
      ? subset.isCheckedRule(isChecked)
      : isChecked;
    const updatedSubset = Object.assign({}, subset, {checked});
    return updatedSubset;
  });
}

// turn a base capability + a list of ux permissions
// and return a full permissions object
export function buildPermissionsFromRequest(
  baseRole: BaseRoleNameValues,
  requestPermissions?: RequestPermissions
) {
  // Valid Base role has to be there
  if (!baseRole || !isValidCustomBaseRoleName(baseRole)) {
    throw new Error('invalid core capability');
  }

  const basePermissions: SitePermissions = getBasePermissions(baseRole);
  const writableBasePermissions = structuredClone(basePermissions);

  // Accounting for the case when only base role is selected
  if (!requestPermissions) {
    return writableBasePermissions;
  }

  const propertiesList: Array<PermissionParameters> = getCheckedProperties(
    requestPermissions,
    baseRole
  );

  propertiesList.forEach((property: PermissionParameters) => {
    const {resource, action, checked} = property;
    const baseResource: ResourceSet = writableBasePermissions[resource] || {};
    baseResource[action] = checked;
    // @ts-expect-error this is a valid assignment
    writableBasePermissions[resource] = baseResource;
  });

  return writableBasePermissions;
}

const SPLIT_CONTROLLED_PERMISSIONS: ReadonlyArray<AdditionalPermission> = [
  AdditionalPermissionNames.EDIT_MAIN_SITE_WITHOUT_APPROVAL,
  AdditionalPermissionNames.EDIT_CMS_ITEMS,
  AdditionalPermissionNames.MAKE_CHANGES,
];

export type SplitRuleChange = {
  permission: AdditionalPermission;
  resource: string;
  action: string;
  from: boolean | undefined;
  to: boolean | undefined;
};

type RuleValues = Record<string, Record<string, boolean> | undefined>;

/**
 * Finds the rules a save changes for a checkbox the admin did not change.
 * "Edit main site directly", "Edit CMS items" and "Make changes without
 * approval" can't all show a role that was saved with only some of them, so a
 * save can rewrite rules the admin never touched, for example after the flag
 * is turned off. A box is untouched when the request sends the state the
 * stored role already reads back as, derived the way the role editor does it:
 * every rule of the box matches.
 *
 * Only boxes in the request that the base role offers are looked at, since a
 * box that isn't sent writes nothing.
 */
export function getUntouchedSplitRuleChanges({
  baseRole,
  requestPermissions,
  storedPermissions,
  builtPermissions,
}: {
  baseRole: BaseRoleNameValues;
  requestPermissions: RequestPermissions | undefined;
  storedPermissions: SitePermissions;
  builtPermissions: SitePermissions;
}): Array<SplitRuleChange> {
  if (!requestPermissions) {
    return [];
  }

  const stored = storedPermissions as unknown as RuleValues;
  const built = builtPermissions as unknown as RuleValues;
  const changes = new Map<string, SplitRuleChange>();

  SPLIT_CONTROLLED_PERMISSIONS.forEach((permissionName) => {
    const requested = requestPermissions[permissionName];
    const permission = ADDITIONAL_PERMISSIONS_LIST.find(
      ([name]) => name === permissionName
    );
    if (
      requested === undefined ||
      !permission ||
      !(permission[1].uxBaseRoleFilter?.(baseRole) ?? true)
    ) {
      return;
    }

    const [, {ruleSet, resource, action}] = resolvePermissionForBaseRole(
      permission,
      baseRole
    );
    const rules: Array<PermissionData> =
      ruleSet ?? (resource && action ? [{resource, action}] : []);
    const storedChecked =
      rules.length > 0 &&
      rules.every(
        (rule) =>
          stored[rule.resource]?.[rule.action] ===
          (rule.isCheckedRule ? rule.isCheckedRule(true) : true)
      );
    if (requested !== storedChecked) {
      return;
    }

    rules.forEach((rule) => {
      const key = `${rule.resource}.${rule.action}`;
      const from = stored[rule.resource]?.[rule.action];
      const to = built[rule.resource]?.[rule.action];
      if (from !== to && !changes.has(key)) {
        changes.set(key, {
          permission: permissionName,
          resource: rule.resource,
          action: rule.action,
          from,
          to,
        });
      }
    });
  });

  return [...changes.values()];
}

export function getSeatTypeFromCustomRole(
  role: BaseRoleNameValues
): WorkspaceSeatType {
  if (TIER_CUSTOM_BASE_ROLES.maxFullAccessSeats.includes(role)) return 'full';
  if (TIER_CUSTOM_BASE_ROLES.maxLimitedAccessSeats.includes(role))
    return 'limited';

  return 'free';
}

export function getRankForCustomRole(role: BaseRoleNameValues): number {
  if (TIER_CUSTOM_BASE_ROLES.maxFullAccessSeats.includes(role)) return 2;
  if (TIER_CUSTOM_BASE_ROLES.maxLimitedAccessSeats.includes(role)) return 1;

  return 0;
}

// Base roles that are eligible for client workspace-role assignments. Mirrors
// CLIENT_ALLOWED_SITE_ROLES (page_builder_v2/editor/commenter): clients are
// excluded from full-seat tiers to prevent the seat-gaming risk where an SP
// could create a Designer-equivalent custom role and grant it to a "client".
export const CLIENT_ALLOWED_BASE_ROLES: ReadonlyArray<BaseRoleNameValues> = [
  BaseRoleNames.REVIEW,
  BaseRoleNames.EDIT_CONTENT,
  BaseRoleNames.BUILD_PAGES,
];

export function isClientAllowedCustomRole(role: {
  baseRole: BaseRoleNameValues;
}): boolean {
  return CLIENT_ALLOWED_BASE_ROLES.includes(role.baseRole);
}

export function applyPermissionFilters(
  selectedRole: string,
  [
    permissionName,
    {uxBaseRoleFilter, requiredFeatureFlags, hiddenByFeatureFlags},
  ]: UxPermission,
  featureFlags?: FeatureFlagsMap
) {
  const hasBaseRole = uxBaseRoleFilter ? uxBaseRoleFilter(selectedRole) : true;
  const hasAllFeatureFlags =
    requiredFeatureFlags && requiredFeatureFlags.length > 0
      ? requiredFeatureFlags.every((flag) => Boolean(featureFlags?.[flag]))
      : true;
  const isHiddenByFeatureFlag = Boolean(
    hiddenByFeatureFlags?.some((flag) => Boolean(featureFlags?.[flag]))
  );
  const hasDescription: boolean = Boolean(
    PERMISSION_DESCRIPTIONS[permissionName]
  );

  return (
    hasDescription &&
    hasBaseRole &&
    hasAllFeatureFlags &&
    !isHiddenByFeatureFlag
  );
}

// baseroles map to a set of core permissions
export function getBasePermissions(baseRole: string) {
  switch (baseRole) {
    case BaseRoleNames.REVIEW:
      return CommenterSitePermissions;
    case BaseRoleNames.EDIT_CONTENT:
      return EditorSitePermissions;
    case BaseRoleNames.BUILD_PAGES:
      return MarketerSitePermissions;
    case BaseRoleNames.DESIGN:
      return DesignerSitePermissions;
    default:
      throw new Error('no base permission found');
  }
}
